import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import EnhancedTable from "../../../components/common/EnhancedTable";
import CommonModal from "../../../components/common/CommonModal";
import { useSchoolRequest } from "../../../hooks/useSchoolRequest";
import type { SchoolRequest, SchoolRequestRequestDto } from "../../../hooks/useSchoolRequest";

const toDate = (value: any): Date | null => {
  if (!value) return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === "number") {
    // Backend may send epoch seconds with fractional nanos (e.g. 1777281706.152881000)
    // Detect seconds vs millis by magnitude.
    const n = value;
    const ms = n < 1e12 ? Math.round(n * 1000) : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return null;

    // Numeric epoch as string (seconds or millis, maybe with fraction)
    if (/^\d+(\.\d+)?$/.test(raw)) {
      const n = Number(raw);
      if (Number.isFinite(n)) {
        const ms = n < 1e12 ? Math.round(n * 1000) : n;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }

    // Handles backend LocalDateTime-like serialization: "2026,3,17,11,9,21,371284000"
    if (raw.includes(",")) {
      const parts = raw.split(",").map((p) => Number(p.trim()));
      if (parts.length >= 6 && parts.slice(0, 6).every((n) => Number.isFinite(n))) {
        const [y, m, d, hh, mm, ss] = parts;
        const dt = new Date(y, m - 1, d, hh, mm, ss);
        return Number.isNaN(dt.getTime()) ? null : dt;
      }
    }

    // ISO string with timezone (Z / +05:30 etc.)
    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Common Spring representations: { epochSecond, nano } or { seconds, nanos }
  if (typeof value === "object") {
    const sec = (value.epochSecond ?? value.seconds) as number | undefined;
    const nano = (value.nano ?? value.nanos) as number | undefined;
    if (typeof sec === "number") {
      const ms = sec * 1000 + (typeof nano === "number" ? Math.floor(nano / 1e6) : 0);
      const dt = new Date(ms);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
  }

  return null;
};

const formatDateTime = (value: any): string => {
  const dt = toDate(value);
  if (!dt) return "-";
  // Converts according to user's local timezone automatically
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(dt);
};

const SchoolRequestManagement: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { useSchoolRequests, updateSchoolRequestMutation } = useSchoolRequest();

  const requestsQuery = useSchoolRequests({ page, size: rowsPerPage });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"view" | "edit">("view");
  const [selected, setSelected] = useState<SchoolRequest | null>(null);
  const [formData, setFormData] = useState<SchoolRequestRequestDto>({
    schoolName: "",
    email: "",
    mobileNo: "",
    addressLine1: "",
    addressLine2: "",
    district: "",
    state: "",
    pincode: "",
    status: "PENDING",
    description: "",
  });

  const openView = (row: SchoolRequest) => {
    setSelected(row);
    setDialogMode("view");
    setFormData({
      schoolName: row.schoolName ?? "",
      email: row.email ?? "",
      mobileNo: row.mobileNo ?? "",
      addressLine1: row.addressLine1 ?? "",
      addressLine2: row.addressLine2 ?? "",
      district: row.district ?? "",
      state: row.state ?? "",
      pincode: row.pincode ?? "",
      status: row.status ?? "PENDING",
      description: row.description ?? "",
    });
    setDialogOpen(true);
  };

  const openEdit = (row: SchoolRequest) => {
    setSelected(row);
    setDialogMode("edit");
    setFormData({
      schoolName: row.schoolName ?? "",
      email: row.email ?? "",
      mobileNo: row.mobileNo ?? "",
      addressLine1: row.addressLine1 ?? "",
      addressLine2: row.addressLine2 ?? "",
      district: row.district ?? "",
      state: row.state ?? "",
      pincode: row.pincode ?? "",
      status: row.status ?? "PENDING",
      description: row.description ?? "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const readOnly = dialogMode === "view";

  const normalizedSubmitData = useMemo(
    () => ({
      ...formData,
      description: formData.description?.trim() ? formData.description : "",
      addressLine2: formData.addressLine2?.trim() ? formData.addressLine2 : "",
    }),
    [formData]
  );

  const handleUpdate = async () => {
    if (!selected?.id) return;
    await updateSchoolRequestMutation.mutateAsync({
      id: Number(selected.id),
      data: normalizedSubmitData,
    });
    closeDialog();
  };

  return (
    <Box>
      {requestsQuery.isLoading && <LinearProgress sx={{ mb: 1 }} />}

      {requestsQuery.isError && (
        <Box sx={{ mb: 1 }}>
          <Typography color="error" fontWeight="bold">
            Failed to load school requests.
          </Typography>
        </Box>
      )}

      <EnhancedTable
        title="School Requests"
        columns={[
          { id: "schoolName", label: "School Name", minWidth: 170 },
          { id: "email", label: "Email", minWidth: 200 },
          { id: "mobileNo", label: "Mobile No", minWidth: 140 },
          { id: "district", label: "District", minWidth: 140 },
          { id: "state", label: "State", minWidth: 120 },
          {
            id: "status",
            label: "Status",
            minWidth: 120,
            format: (value) => (
              <Chip
                size="small"
                label={String(value ?? "")}
                color={
                  value === "PENDING"
                    ? "warning"
                    : value === "SUCCESS"
                    ? "success"
                    : value === "DISCUSSION"
                    ? "info"
                    : "default"
                }
                variant="outlined"
              />
            ),
          },
        ]}
        data={requestsQuery.data?.content ?? []}
        searchFields={["schoolName", "email", "mobileNo", "district", "state", "status"]}
        actions={(row: SchoolRequest) => (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                openView(row);
              }}
            >
              View
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={(e) => {
                e.stopPropagation();
                openEdit(row);
              }}
            >
              Update
            </Button>
          </Box>
        )}
        enablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={requestsQuery.data?.totalElements ?? 0}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        loading={requestsQuery.isLoading}
        cardProps={{
          elevation: 0,
          sx: {
            border: "1px solid #e0e0e0",
            "& .MuiCardContent-root": { p: 2 },
          },
        }}
      />

      <CommonModal
        open={dialogOpen}
        onClose={closeDialog}
        title={dialogMode === "view" ? "View School Request" : "Update School Request"}
        maxWidth="md"
        fullWidth
        customActions={
          <>
            <Button onClick={closeDialog} variant="outlined">
              Close
            </Button>
            {dialogMode === "edit" && (
              <Button
                onClick={handleUpdate}
                variant="contained"
                disabled={updateSchoolRequestMutation.isLoading || !selected?.id}
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              >
                {updateSchoolRequestMutation.isLoading ? "Updating..." : "Update"}
              </Button>
            )}
          </>
        }
      >
          {dialogMode === "view" ? (
            <Box sx={{ px: 1 }}>
              <Typography variant="h5" sx={{ mb: 3 }}>
                Request Details
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">School Name:</Typography>
                <Typography>{selected?.schoolName ?? "-"}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">Email:</Typography>
                <Typography>{selected?.email ?? "-"}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">Mobile No:</Typography>
                <Typography>{selected?.mobileNo ?? "-"}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">Address:</Typography>
                <Typography>
                  {[
                    selected?.addressLine1,
                    selected?.addressLine2,
                    selected?.district,
                    selected?.state,
                    selected?.pincode,
                  ]
                    .filter((v) => String(v ?? "").trim())
                    .join(", ") || "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">Status:</Typography>
                <Typography>{selected?.status ?? "-"}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">Request Date:</Typography>
                <Typography>{formatDateTime(selected?.createdAt)}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Typography fontWeight="bold">Description:</Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{selected?.description ?? "-"}</Typography>
              </Box>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="School Name"
                  value={formData.schoolName}
                  onChange={(e) => setFormData((p) => ({ ...p, schoolName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mobile No"
                  value={formData.mobileNo}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, mobileNo: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                  }
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={formData.pincode}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))
                  }
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData((p) => ({ ...p, addressLine1: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={formData.addressLine2 ?? ""}
                  onChange={(e) => setFormData((p) => ({ ...p, addressLine2: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="District"
                  value={formData.district}
                  onChange={(e) => setFormData((p) => ({ ...p, district: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData((p) => ({ ...p, state: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={formData.status}
                    onChange={(e) => setFormData((p) => ({ ...p, status: String(e.target.value) }))}
                  >
                    <MenuItem value="PENDING">PENDING</MenuItem>
                    <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                    <MenuItem value="DISCUSSION">DISCUSSION</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                  multiline
                  minRows={4}
                />
              </Grid>
            </Grid>
          )}
      </CommonModal>
    </Box>
  );
};

export default SchoolRequestManagement;

